import { useState, useRef, useCallback, useEffect } from 'react';
import { DeckInsertionPosition, DragState, Position, TabletopState, SelectedItem, TabletopItemType, ZoomState } from '../types';
import { calculateDeckHoverTarget, getScaledMousePosition } from '../utils/positionUtils';
import { orderCardIdsForInteraction } from '../utils/cardLayering';
import { getStackedCardPositions } from '../utils/cardStacking';
import { CARD_WIDTH, CARD_HEIGHT, DRAG_DEBOUNCE_THRESHOLD, POSITION_THRESHOLD } from '../constants';

interface UseDragAndDropProps {
  tabletopState: TabletopState;
  setTabletopState: React.Dispatch<React.SetStateAction<TabletopState>>;
  setSelectedItem: React.Dispatch<React.SetStateAction<SelectedItem | null>>;
  addCardToDeck: (cardId: string, deckId: string, position?: DeckInsertionPosition) => void;
  moveTableItem: (itemType: 'card' | 'deck' | 'dice' | 'token' | 'coin', id: string, position: Position) => void;
  moveCardGroup: (cardIds: string[], positions: Record<string, Position>) => void;
  zoomState: ZoomState;
  handBounds: DOMRect | null;
  moveCardToHand: (cardId: string) => void;
  setSelectedCardIds: React.Dispatch<React.SetStateAction<string[]>>;
  selectedCardIds: string[];
  onCardDropped?: (info: { id: string; type: TabletopItemType; origin: 'table' | 'hand' | null; dropTarget: 'table' | 'deck' | 'hand' }) => void;
  onHandHoverChange?: (isHovering: boolean) => void;
  invertView?: boolean;
}

export const useDragAndDrop = ({
  tabletopState,
  setTabletopState,
  setSelectedItem,
  addCardToDeck,
  moveTableItem,
  moveCardGroup,
  zoomState,
  handBounds,
  moveCardToHand,
  selectedCardIds,
  setSelectedCardIds,
  onCardDropped,
  onHandHoverChange,
  invertView = false
}: UseDragAndDropProps) => {
  // Drag state
  const [dragStateInternal, setDragStateInternal] = useState<DragState>({
    isDragging: false,
    dragStart: { x: 0, y: 0 },
    draggingItem: null,
    cardOffset: { x: 0, y: 0 },
    hoveringDeckId: null,
    hoveringDeckZone: null,
    origin: null,
    hoveringHand: false,
    groupOffsets: undefined
  });
  const dragStateRef = useRef<DragState>(dragStateInternal);

  useEffect(() => {
    dragStateRef.current = dragStateInternal;
  }, [dragStateInternal]);

  const setDragState = useCallback(
    (updater: DragState | ((prev: DragState) => DragState)) => {
      setDragStateInternal(prev => {
        const next = typeof updater === 'function' ? (updater as (p: DragState) => DragState)(prev) : updater;
        dragStateRef.current = next;
        return next;
      });
    },
    [setDragStateInternal]
  );

  // Create refs for the last positions to debounce updates
  const lastDragPositionRef = useRef<{ x: number, y: number } | null>(null);
  const lastFrameTimeRef = useRef<number>(0);

  // Generic item selection handler
  const handleItemSelect = useCallback((id: string, type: TabletopItemType) => {
    setSelectedItem({ id, type });
  }, [setSelectedItem]);

  // Get item collection based on type
  const getItemCollection = useCallback((type: TabletopItemType) => {
    switch (type) {
      case 'card': return tabletopState.cards;
      case 'deck': return tabletopState.decks;
      case 'dice': return tabletopState.dice;
      case 'token': return tabletopState.tokens || {};
      case 'coin': return tabletopState.coins || {};
      default: return {};
    }
  }, [tabletopState]);

  // Update item position in tabletop state
  const updateItemPosition = useCallback((id: string, type: TabletopItemType, position: Position) => {
    setTabletopState(prev => {
      const newState = { ...prev };

      switch (type) {
        case 'card':
          newState.cards = {
            ...prev.cards,
            [id]: { ...prev.cards[id], position }
          };
          break;
        case 'deck':
          newState.decks = {
            ...prev.decks,
            [id]: { ...prev.decks[id], position }
          };
          break;
        case 'dice':
          newState.dice = {
            ...prev.dice,
            [id]: { ...prev.dice[id], position }
          };
          break;
        case 'token':
          newState.tokens = {
            ...prev.tokens,
            [id]: { ...prev.tokens[id], position }
          };
          break;
        case 'coin':
          newState.coins = {
            ...(prev.coins ?? {}),
            [id]: { ...(prev.coins ?? {})[id], position }
          };
          break;
      }
      
      return newState;
    });
  }, [setTabletopState]);
  
  const updateCardGroupPositions = useCallback((positions: Record<string, Position>) => {
    if (Object.keys(positions).length === 0) {
      return;
    }

    setTabletopState(prev => {
      const nextCards = { ...prev.cards };
      let changed = false;

      Object.entries(positions).forEach(([cardId, position]) => {
        const existing = nextCards[cardId];
        if (!existing) return;
        nextCards[cardId] = { ...existing, position };
        changed = true;
      });

      if (!changed) {
        return prev;
      }

      return {
        ...prev,
        cards: nextCards
      };
    });
  }, [setTabletopState]);
  
  // Item drag start handler
  const handleItemDragStart = useCallback((id: string, type: TabletopItemType, event: React.MouseEvent, containerRect: DOMRect) => {
    event.stopPropagation();
    event.preventDefault();
    
    // Set dragging item state
    setDragState(prev => ({
      ...prev,
      isDragging: true,
      draggingItem: { id, type },
      origin: 'table',
      hoveringHand: false,
      hoveringDeckId: null,
      hoveringDeckZone: null,
      groupOffsets: undefined
    }));
    if (onHandHoverChange) {
      onHandHoverChange(false);
    }
    
    // Select the item being dragged
    setSelectedItem({ id, type });
    
    // Get the mouse position in the original, unscaled coordinate system
    const cursorPos = getScaledMousePosition(event, containerRect, zoomState.position, zoomState.scale, invertView);
    
    // Get the item collection based on type
    const itemCollection = getItemCollection(type);
    const item = itemCollection[id];
    
    if (!item) return;
    
    let groupOffsets: Record<string, Position> | undefined;
    if (type === 'card') {
      const baseSelection = selectedCardIds.includes(id) ? selectedCardIds : [id];
      const orderedSelection = orderCardIdsForInteraction(baseSelection, tabletopState.cards, id);
      const offsets: Record<string, Position> = {};
      orderedSelection.forEach(cardId => {
        const cardState = tabletopState.cards[cardId];
        if (!cardState) return;
        offsets[cardId] = {
          x: cardState.position.x - item.position.x,
          y: cardState.position.y - item.position.y
        };
      });
      const groupIds = Object.keys(offsets);
      if (groupIds.length > 1) {
        setSelectedCardIds(groupIds);
        groupOffsets = offsets;
        setDragState(prev => ({
          ...prev,
          draggingItem: { id, type, groupIds },
          groupOffsets
        }));
      } else if (!selectedCardIds.includes(id) && !event.shiftKey) {
        setSelectedCardIds([id]);
      }
    }

    // Calculate the offset (distance from cursor to item's top-left corner)
    const cardOffset = {
      x: cursorPos.x - item.position.x,
      y: cursorPos.y - item.position.y,
    };
    
    setDragState(prev => ({
      ...prev,
      cardOffset,
      groupOffsets: groupOffsets ?? prev.groupOffsets
    }));
    
  }, [tabletopState, zoomState, invertView, setSelectedItem, setTabletopState, getItemCollection, setDragState, onHandHoverChange, selectedCardIds, setSelectedCardIds]);

  // Check if position update should be processed (debouncing logic)
  const shouldUpdatePosition = useCallback((newPosition: Position): boolean => {
    const now = performance.now();
    
    if (!lastDragPositionRef.current || 
        now - lastFrameTimeRef.current > DRAG_DEBOUNCE_THRESHOLD ||
        Math.abs(newPosition.x - (lastDragPositionRef.current?.x || 0)) > POSITION_THRESHOLD ||
        Math.abs(newPosition.y - (lastDragPositionRef.current?.y || 0)) > POSITION_THRESHOLD) {
      
      lastDragPositionRef.current = newPosition;
      lastFrameTimeRef.current = now;
      return true;
    }
    
    return false;
  }, []);

  // Handle mouse move (for dragging)
  const handleMouseMove = useCallback((e: React.MouseEvent, containerRect: DOMRect) => {
    const currentDragState = dragStateRef.current;
    const { draggingItem, cardOffset, groupOffsets } = currentDragState;
  
    if (!draggingItem) return;
    
    // Get the mouse position in the tabletop coordinate system
    const { x: cursorX, y: cursorY } = getScaledMousePosition(
      e, 
      containerRect, 
      zoomState.position, 
      zoomState.scale,
      invertView
    );
    
    // Calculate new position
    const newPosition = {
      x: cursorX - cardOffset.x,
      y: cursorY - cardOffset.y,
    };
    
    // Prevent default to avoid text selection and other browser behaviors
    e.preventDefault();
    e.stopPropagation();
    
    // Special case for cards: check hovering over deck
    let hoveredDeckId: string | null = null;
    let hoveredDeckZone: DeckInsertionPosition | null = null;
    let hoveringHand = false;

    if (draggingItem.type === 'card') {
      const hoverTarget = calculateDeckHoverTarget(newPosition, tabletopState.decks, CARD_WIDTH, CARD_HEIGHT);
      hoveredDeckId = hoverTarget.deckId;
      hoveredDeckZone = hoverTarget.zone;
      if (handBounds) {
        hoveringHand =
          e.clientX >= handBounds.left &&
          e.clientX <= handBounds.right &&
          e.clientY >= handBounds.top &&
          e.clientY <= handBounds.bottom;
      }
    }

    setDragState(prev => {
      const deckChanged = prev.hoveringDeckId !== hoveredDeckId;
      const zoneChanged = prev.hoveringDeckZone !== hoveredDeckZone;
      const handChanged = prev.hoveringHand !== hoveringHand;
      if (!deckChanged && !zoneChanged && !handChanged) {
        return prev;
      }
      if (handChanged && onHandHoverChange) {
        onHandHoverChange(hoveringHand);
      }
      return {
        ...prev,
        hoveringDeckId: hoveredDeckId,
        hoveringDeckZone: hoveredDeckZone,
        hoveringHand
      };
    });
    
    // Update item position with debouncing
    if (shouldUpdatePosition(newPosition)) {
      if (draggingItem.type === 'card' && groupOffsets && Object.keys(groupOffsets).length > 1) {
        const positions: Record<string, Position> = {};
        if (e.altKey) {
          const groupIds = draggingItem.groupIds ?? Object.keys(groupOffsets);
          const layout = getStackedCardPositions(groupIds, tabletopState.cards, 'compact', newPosition);
          Object.assign(positions, layout.positions);
        } else {
          Object.entries(groupOffsets).forEach(([cardId, offset]) => {
            positions[cardId] = {
              x: newPosition.x + offset.x,
              y: newPosition.y + offset.y
            };
          });
        }
        updateCardGroupPositions(positions);
      } else {
        updateItemPosition(draggingItem.id, draggingItem.type, newPosition);
      }
    }
  }, [tabletopState, zoomState, invertView, updateItemPosition, updateCardGroupPositions, shouldUpdatePosition, setDragState, handBounds, onHandHoverChange]);

  // Handle mouse up (end dragging)
  const handleMouseUp = useCallback((e: React.MouseEvent, containerRect: DOMRect) => {
    const currentDragState = dragStateRef.current;
    const { draggingItem, hoveringDeckId, hoveringDeckZone, cardOffset, groupOffsets } = currentDragState;
    
    // Reset the debouncing refs
    lastDragPositionRef.current = null;
    
    if (!draggingItem) return;
    
    // Get current mouse position
    const { x: cursorX, y: cursorY } = getScaledMousePosition(
      e, 
      containerRect, 
      zoomState.position, 
      zoomState.scale,
      invertView
    );
    
    const newPosition = {
      x: cursorX - cardOffset.x,
      y: cursorY - cardOffset.y,
    };
    
    let dropTarget: 'table' | 'deck' | 'hand' = 'table';
    let finalPosition = newPosition;
    let shouldCommitMove = true;
    const isMovableType =
      draggingItem.type === 'card' ||
      draggingItem.type === 'deck' ||
      draggingItem.type === 'dice' ||
      draggingItem.type === 'token' ||
      draggingItem.type === 'coin';
    
    // Handle card drop on deck
    if (draggingItem.type === 'card' && hoveringDeckId) {
      const insertionZone: DeckInsertionPosition = hoveringDeckZone ?? 'top';
      if (groupOffsets && Object.keys(groupOffsets).length > 1) {
        const groupIds = draggingItem.groupIds ?? Object.keys(groupOffsets);
        const orderedGroupIds =
          insertionZone === 'bottom' ? [...groupIds].reverse() : groupIds;
        orderedGroupIds.forEach(cardId => addCardToDeck(cardId, hoveringDeckId, insertionZone));
      } else {
        addCardToDeck(draggingItem.id, hoveringDeckId, insertionZone);
      }
      dropTarget = 'deck';
      shouldCommitMove = false;
    } 
    // Handle card drop back into hand area
    else if (
      draggingItem.type === 'card' &&
      handBounds &&
      e.clientX >= handBounds.left &&
      e.clientX <= handBounds.right &&
      e.clientY >= handBounds.top &&
      e.clientY <= handBounds.bottom
    ) {
      if (groupOffsets && Object.keys(groupOffsets).length > 1) {
        Object.keys(groupOffsets).forEach(cardId => moveCardToHand(cardId));
        setSelectedCardIds([]);
      } else {
        moveCardToHand(draggingItem.id);
        setSelectedCardIds([]);
      }
      dropTarget = 'hand';
      shouldCommitMove = false;
    }
    // Handle free drop on table
    else {
      if (!(draggingItem.type === 'card' && groupOffsets && Object.keys(groupOffsets).length > 1)) {
        updateItemPosition(draggingItem.id, draggingItem.type, finalPosition);
      }
    }

    if (shouldCommitMove && isMovableType) {
      if (draggingItem.type === 'card' && groupOffsets && Object.keys(groupOffsets).length > 1) {
        const orderedGroupIds = draggingItem.groupIds ?? Object.keys(groupOffsets);
        if (e.altKey) {
          const layout = getStackedCardPositions(orderedGroupIds, tabletopState.cards, 'compact', finalPosition);
          moveCardGroup(layout.cardIds, layout.positions);
        } else {
          const positions = orderedGroupIds.reduce<Record<string, Position>>((acc, cardId) => {
            const offset = groupOffsets[cardId];
            if (!offset) {
              return acc;
            }

            acc[cardId] = {
              x: newPosition.x + offset.x,
              y: newPosition.y + offset.y
            };
            return acc;
          }, {});

          moveCardGroup(orderedGroupIds, positions);
        }
      } else {
        moveTableItem(draggingItem.type as 'card' | 'deck' | 'dice' | 'token' | 'coin', draggingItem.id, finalPosition);
      }
    }
    
    // Clear dragging and hover state immediately
    setDragState(prev => {
      if (onHandHoverChange && prev.hoveringHand) {
        onHandHoverChange(false);
      }
      return {
        ...prev,
        draggingItem: null,
        hoveringDeckId: null,
        hoveringDeckZone: null,
        hoveringHand: false,
        isDragging: false,
        origin: null,
        groupOffsets: undefined
      };
    });
    
    if (onCardDropped) {
      onCardDropped({
        id: draggingItem.id,
        type: draggingItem.type,
        origin: currentDragState.origin,
        dropTarget
      });
    }
    
  }, [zoomState, invertView, addCardToDeck, updateItemPosition, handBounds, moveCardToHand, moveTableItem, moveCardGroup, tabletopState.cards, onCardDropped, setDragState, onHandHoverChange, setSelectedCardIds]);

  return {
    dragState: dragStateInternal,
    setDragState,
    handleItemSelect,
    handleItemDragStart,
    handleMouseMove,
    handleMouseUp,
    updateItemPosition
  };
}; 
